import { CircleCheck } from '@parrot-software/design-system/icons';
import { cn } from '@parrot-software/utils';

import { confirmationSuccessHeaderStyles as styles } from './ConfirmationSuccessHeader.styles';

type ConfirmationSuccessHeaderProps = {
  className?: string;
  iconSize?: number;
  layout?: 'inline' | 'stacked';
  subtitle?: string;
  title: string;
};

/**
 * Shared success header for the three terminal confirmation outcomes of wizard
 * step 5. Renders the green check icon plus a title (and optional subtitle),
 * mirroring the Figma confirmation designs.
 *
 * The `inline` layout (icon + title on one row) matches the SPEI confirmation
 * (F1); the `stacked` layout (icon on top, centered title + subtitle below)
 * matches the card confirmations (F2).
 *
 * @param props.className - Extra classes merged onto the root (e.g. responsive alignment)
 * @param props.iconSize - Pixel size of the check icon (defaults to 28)
 * @param props.layout - `inline` (icon beside title) or `stacked` (icon above centered title)
 * @param props.subtitle - Optional supporting line rendered under the title
 * @param props.title - Success title text
 * @returns React element rendering the confirmation success header
 */
export const ConfirmationSuccessHeader = ({
  className,
  iconSize = 28,
  layout = 'inline',
  subtitle,
  title,
}: ConfirmationSuccessHeaderProps) => {
  const isStacked = layout === 'stacked';

  return (
    <div className={cn({ [styles.rootStacked]: isStacked, [styles.rootInline]: !isStacked }, className)}>
      <CircleCheck aria-hidden className={styles.icon} size={iconSize} />
      <div className={cn({ [styles.textBoxStacked]: isStacked, [styles.textBoxInline]: !isStacked })}>
        <p className={styles.title}>{title}</p>
        {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
      </div>
    </div>
  );
};
